import { Trash2 } from 'lucide-react'
import { useState } from 'react'

import { useComments, useCreateComment, useDeleteComment } from '@/api/hooks'
import type { Comment } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { fr } from '@/i18n/fr'
import { formatDateTime } from '@/lib/format'

/**
 * The conversation about a recipe (F-16) — the half of F-7 that makes ownership a division
 * of labour rather than a restriction. Anyone may write here, including on a recipe they
 * cannot edit, which is why nothing on this screen consults `can_edit`.
 *
 * A comment cannot be edited (F-18): there is no pencil, only the author's bin. Whether that
 * bin appears is `can_delete`, computed server-side and read off the payload — never
 * re-derived from `author` here, for the same reason `can_edit` isn't (NFR-37).
 */
export function CommentThread({ recipeId }: { recipeId: number }) {
  const { data: comments, isPending } = useComments(recipeId)
  const createComment = useCreateComment(recipeId)
  const deleteComment = useDeleteComment(recipeId)
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Comment | null>(null)

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const trimmed = body.trim()
    if (!trimmed) return
    setError(null)
    try {
      await createComment.mutateAsync(trimmed)
      setBody('')
    } catch {
      // The mutation has to be awaited *and* caught: an unhandled rejection here is a
      // comment that silently never posted, which is worse than an error message.
      setError(fr.comments.failed)
    }
  }

  function confirmDelete() {
    if (!pendingDelete) return
    deleteComment.mutate(pendingDelete.id, {
      onError: () => setError(fr.common.error),
      onSettled: () => setPendingDelete(null),
    })
  }

  return (
    <section className="mt-10 border-t border-stone-200 pt-6 dark:border-stone-800">
      <h2 className="text-xl font-semibold">
        {fr.comments.heading}
        {comments && comments.length > 0 && (
          <span className="ml-2 text-base font-normal text-stone-500">{comments.length}</span>
        )}
      </h2>

      {isPending ? (
        <p className="mt-3 text-stone-500">{fr.common.loading}</p>
      ) : comments && comments.length > 0 ? (
        <ul className="mt-3 flex flex-col">
          {comments.map((comment) => (
            <li
              key={comment.id}
              className="border-t border-stone-200 py-3 first:border-t-0 first:pt-0 dark:border-stone-800"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  <span className="font-medium text-stone-700 dark:text-stone-300">
                    {/* NFR-39: a name, or a neutral label — never something address-shaped. */}
                    {comment.author ?? fr.comments.formerMember}
                  </span>{' '}
                  · <time dateTime={comment.created_at}>{formatDateTime(comment.created_at)}</time>
                </p>
                {comment.can_delete && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPendingDelete(comment)}
                    aria-label={fr.comments.delete}
                    className="-mt-2 shrink-0 text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {/* Line breaks the writer typed are part of what they wrote. */}
              <p className="mt-1 whitespace-pre-wrap">{comment.body}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-stone-500">{fr.comments.empty}</p>
      )}

      <form onSubmit={handleSubmit} className="mt-4 flex flex-col items-end gap-2">
        <Textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder={fr.comments.placeholder}
          aria-label={fr.comments.placeholder}
          rows={3}
        />
        {error && <p className="self-start text-sm text-red-600">{error}</p>}
        <Button type="submit" disabled={!body.trim() || createComment.isPending}>
          {createComment.isPending ? fr.comments.submitting : fr.comments.submit}
        </Button>
      </form>

      {/* Deleting asks, unlike the ingredient row's bin. NFR-31 rejected a confirmation
          dialog for recipes because it was offered *instead of* an undo — here there is no
          undo at all (F-18), so the dialog is the only thing between a mis-tap and a
          sentence nobody can get back. */}
      <Dialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{fr.comments.deleteConfirm.title}</DialogTitle>
            <DialogDescription>{fr.comments.deleteConfirm.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              {fr.common.cancel}
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteComment.isPending}>
              {fr.comments.deleteConfirm.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
